import React, {FunctionComponent, useEffect, useMemo, useState} from 'react';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {AStatusActionCard, AStatusActionCardOption} from '@atiautomacao/ati-ui-library';
import {
    CommandDTO,
    ModalForSendingTelecommand,
} from '../../../../../../Shared/Components/ModalForSendingTelecommand';
import {findAllCommands} from '../../../../../../Utils/CommandService';
import {useAppSelector} from '../../../../../../Config/Hooks';
import {hasPermission} from '../../../../../../Shared/Auth/AuthenticationUtil';
import {AUTHORITIES} from '../../../../../../Config/Constants';
import {MenuItemStatus} from '../../../../../../Shared/Components/Layout/menu/types/MenuItemStatus';
import {
    COMMANDS_CARD_CONTENT,
    isWithoutCommunication,
    resolveCommandsCardState,
} from './commandsCardState';

export interface InverterSimpleCommandsCardProps {
    equipmentId: number;
    status?: MenuItemStatus;
}

/**
 * Card de comandos da tela simplificada de inversor.
 *
 * Container do `AStatusActionCard` da ati-ui-library: aqui ficam a busca dos telecomandos, a
 * checagem de permissão, a resolução do estado do card e o modal de envio. A apresentação — faixa
 * com ícone, textos e botão que abre a lista de comandos — vem da biblioteca.
 */
const InverterSimpleCommandsCard: FunctionComponent<InverterSimpleCommandsCardProps> = ({
    equipmentId,
    status,
}) => {
    const account = useAppSelector((state) => state.authentication.account);
    const isAuthorizedToSendCommand = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN,
        AUTHORITIES.SEND_POWER_STATION_EQUIPMENT_DETAIL_COMMAND,
        AUTHORITIES.SEND_SKID_EQUIPMENT_DETAIL_COMMAND,
    ]);

    // Sem comunicação ou sem permissão o card já está resolvido — não vale buscar comandos.
    const shouldFetchCommands =
        equipmentId > 0 && !isWithoutCommunication(status) && isAuthorizedToSendCommand;

    const [teleCommandList, setTeleCommandList] = useState<CommandDTO[]>([]);
    const [isLoadingCommands, setIsLoadingCommands] = useState(false);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [commandDTO, setCommandDTO] = useState<CommandDTO | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        setTeleCommandList([]);
        setCommandDTO(null);
        setIsModalOpen(false);
        setHasLoadError(false);

        if (!shouldFetchCommands) {
            setIsLoadingCommands(false);
            return;
        }

        let cancelled = false;
        setIsLoadingCommands(true);

        findAllCommands(equipmentId)
            .then((response: any) => {
                if (cancelled) {
                    return;
                }
                // `findAllCommands` normaliza "sem comandos" (404/204) para 204 com lista vazia
                // e devolve o próprio erro quando a requisição falha de fato.
                if (response?.status === 200 || response?.status === 204) {
                    setTeleCommandList(response.data?.data ?? []);
                } else {
                    setTeleCommandList([]);
                    setHasLoadError(true);
                }
            })
            .catch((error: any) => {
                if (!cancelled) {
                    console.error('Error fetching tele commands:', error);
                    setTeleCommandList([]);
                    setHasLoadError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoadingCommands(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [equipmentId, shouldFetchCommands]);

    const cardState = useMemo(
        () =>
            resolveCommandsCardState({
                commandCount: teleCommandList.length,
                hasLoadError,
                isAuthorized: isAuthorizedToSendCommand,
                status,
            }),
        [teleCommandList.length, hasLoadError, isAuthorizedToSendCommand, status]
    );

    const isEnabled = cardState === 'ENABLED';
    const content = COMMANDS_CARD_CONTENT[cardState];

    const options = useMemo<AStatusActionCardOption[]>(
        () => teleCommandList.map((command) => ({id: command.id, label: command.teleObjectName})),
        [teleCommandList]
    );

    const handleSelectOption = (option: AStatusActionCardOption) => {
        const selectedCommand = teleCommandList.find((command) => command.id === option.id);
        if (selectedCommand == null) {
            return;
        }
        setCommandDTO(selectedCommand);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCommandDTO(null);
    };

    return (
        <>
            <AStatusActionCard
                ariaLabel="inverter-simple-commands"
                buttonAriaLabel="Comandos do equipamento"
                buttonLabel={content.buttonLabel}
                buttonTestId="inverter-simple-commands-button"
                disabled={!isEnabled}
                icon={isEnabled ? <ArrowCircleRightOutlinedIcon /> : <ErrorOutlineIcon />}
                loading={isLoadingCommands}
                menuAriaLabel="Comandos disponíveis"
                menuTitle="Comandos"
                onSelectOption={handleSelectOption}
                options={options}
                subText={content.subText}
                text={content.text}
                tone={isEnabled ? 'info' : 'muted'}
            />

            {commandDTO != null && (
                <ModalForSendingTelecommand
                    commandDTO={commandDTO}
                    onCloseModal={handleCloseModal}
                    open={isModalOpen}
                />
            )}
        </>
    );
};

export default InverterSimpleCommandsCard;
